import type {
  FloorPlanData,
  FloorPlanTableData,
  TableShape,
} from '@shared/types/floorPlans';
import { api } from 'restaurantAdmin/api';
import type { ServiceStatus } from 'restaurantAdmin/reservations/service/serviceStatus/ServiceStatus';

export interface SeatedOccupant {
  firstName?: string;
  guestCount: number;
  id: string;
  lastName?: string;
  serviceStatus: ServiceStatus;
  type: 'reservation' | 'walkIn';
}

export interface AssignedServer {
  id: string;
  name: string;
  hexColor: string;
}

export interface HostFloorPlanTable extends FloorPlanTableData {
  mergeTableId: string | null;
  mergeTableName: string | null;
  seatedOccupant: SeatedOccupant | null;
  assignedServer: AssignedServer | null;
}

export interface HostFloorPlanData {
  id: string;
  name: string;
  backgroundSrc?: string;
  floorPlanTables: HostFloorPlanTable[];
}

export const getFloorPlansForRestaurantId = async (
  restaurantId: string,
  date?: string,
): Promise<FloorPlanData[]> => {
  const params = date ? `?${new URLSearchParams({ date })}` : '';
  return api.get<FloorPlanData[]>(
    `/restaurants/${restaurantId}/floor-plans${params}`,
  );
};

export const getHostFloorPlansForRestaurantId = async (
  restaurantId: string,
  date?: string,
): Promise<HostFloorPlanData[]> => {
  const params = date ? `?${new URLSearchParams({ date })}` : '';
  const floorPlans: HostFloorPlanData[] = await api.get(
    `/restaurants/${restaurantId}/admin-floor-plans/seating${params}`,
  );

  return floorPlans;
};

export interface TableTimer {
  floorPlanTableId: string;
  timer: string;
  upcomingReservation: {
    date: string;
    time: string;
  };
}

export const getTableTimers = async (
  restaurantId: string,
): Promise<TableTimer[]> =>
  api.get(`/restaurants/${restaurantId}/admin-floor-plans/table-timers`);

export const unassignAllServers = (restaurantId: string): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/admin-floor-plans/unassign-all-servers`,
  );

export interface GetTableShapesParams {
  description?: string;
  iconName?: string;
  /** Matches when description OR iconName contains the string (partial). */
  search?: string;
  excludeIconNames?: string[];
}

export const getTableShapes = async (
  params?: GetTableShapesParams,
): Promise<TableShape[]> => {
  const searchParams = new URLSearchParams();
  if (params?.description != null && params.description !== '')
    searchParams.set('description', params.description);
  if (params?.iconName != null && params.iconName !== '')
    searchParams.set('iconName', params.iconName);
  if (params?.search != null && params.search !== '')
    searchParams.set('search', params.search);
  if (params?.excludeIconNames != null && params.excludeIconNames.length > 0) {
    searchParams.set('excludeIconNames', params.excludeIconNames.join(','));
  }
  const query = searchParams.toString();
  return api.get<TableShape[]>(
    query ? `/table-shapes?${query}` : '/table-shapes',
  );
};
